import { FC, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
    Button,
    Loading,
    LoadingButton,
    Paper,
    Stack,
    Typography,
} from '@thirdspacelearning/library';
import { dayjs } from '@tsl-frontend/tsl-utilities';

import RestartLessonDialog from './RestartLessonDialog';

import { ILearningObjective } from '../../types';

import { initiateSelfLaunchedSession } from '@api';

import { appLoadedSelector, loadAppData } from '@store/initialisation';
import { currSessionPlannedAtSelector } from '@store/session';
import { useAppDispatch } from '@store';

import {
    LoCompleteIcon,
    LoIncompleteIcon,
    LoStartedIcon,
} from '@assets/dashboard/los-panel';
import { useIntroLoCompleted } from '@hooks';

const sx = {
    item: {
        gap: 1,
        p: 2,
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
    },
    itemInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        flexGrow: 1,
        gap: 2,
    },
    icon: {
        fontSize: '3.5rem',
    },
};

type Props = {
    lo: ILearningObjective;
    isAnySessionInitiating: boolean;
    onSessionInitiationChange: (isInitiating: boolean) => void;
};

const LearningObjectiveItem: FC<Props> = ({
    lo,
    isAnySessionInitiating,
    onSessionInitiationChange,
}) => {
    const {
        adHocSessionsOnStudentExperience,
        showResumeLessonOnStudentExperience,
    } = useFlags();

    const dispatch = useAppDispatch();
    const sessionPlannedAt = useSelector(currSessionPlannedAtSelector);
    const isSessionLoadingPending = useSelector(appLoadedSelector);

    const introLoCompleted = useIntroLoCompleted();

    const [restartDialogOpen, setRestartDialogOpen] = useState(false);

    const loCompleted = Boolean(lo.completed_at);
    const loStarted = lo.started;

    const {
        mutate: createSelfLaunchSession,
        isPending: isFetchingSelfLaunchedSession,
    } = useMutation({
        mutationFn: (resume?: boolean) => {
            onSessionInitiationChange(true);
            return initiateSelfLaunchedSession(lo.subject_id, resume);
        },
        onSuccess: () => {
            dispatch(loadAppData());
            onSessionInitiationChange(false);
        },
        onError: (error: Error) => {
            enqueueSnackbar('Please try again or refresh the page', {
                title: error.message,
                testId: 'self-launch-error',
                variant: 'sxSnackbar',
                severity: 'error',
            });
            onSessionInitiationChange(false);
        },
    });

    const isSessionWithinNextHour = useMemo(() => {
        if (!sessionPlannedAt) {
            return false;
        }

        const now = dayjs();
        const sessionEnd = sessionPlannedAt.add(1, 'hour');
        const oneHourBeforeSession = sessionPlannedAt.subtract(1, 'hour');

        return now.isAfter(oneHourBeforeSession) && now.isBefore(sessionEnd);
    }, [sessionPlannedAt]);

    const isButtonDisabled =
        isSessionWithinNextHour ||
        isAnySessionInitiating ||
        isSessionLoadingPending ||
        !introLoCompleted;

    return (
        <Paper variant="outlined" data-testid="learning-objective-item">
            <Stack sx={sx.item}>
                <Stack sx={sx.itemInfo}>
                    {loCompleted ? (
                        <LoCompleteIcon sx={sx.icon} />
                    ) : showResumeLessonOnStudentExperience && loStarted ? (
                        <LoStartedIcon sx={sx.icon} />
                    ) : (
                        <LoIncompleteIcon sx={sx.icon} />
                    )}
                    <Typography data-testid="learning-objective-title">
                        {lo.subject_title}
                    </Typography>
                </Stack>
                {adHocSessionsOnStudentExperience && (
                    <Stack direction="row" flexShrink={0} gap={1}>
                        {showResumeLessonOnStudentExperience && loStarted ? (
                            <>
                                <Button
                                    disabled={isButtonDisabled}
                                    variant="text"
                                    onClick={() => setRestartDialogOpen(true)}
                                    data-testid="restart-lesson-btn"
                                >
                                    Restart Lesson
                                </Button>
                                <LoadingButton
                                    disabled={isButtonDisabled}
                                    variant="contained"
                                    loading={isFetchingSelfLaunchedSession}
                                    loadingIndicator={<Loading size={24} />}
                                    onClick={() =>
                                        createSelfLaunchSession(true)
                                    }
                                    data-testid="resume-lesson-btn"
                                >
                                    Resume Lesson
                                </LoadingButton>
                            </>
                        ) : (
                            <LoadingButton
                                disabled={isButtonDisabled}
                                variant={
                                    !loCompleted ? 'contained' : 'outlined'
                                }
                                loading={isFetchingSelfLaunchedSession}
                                loadingIndicator={<Loading size={24} />}
                                onClick={() => createSelfLaunchSession(false)}
                                data-testid="start-lesson-btn"
                            >
                                {!loCompleted
                                    ? 'Start Lesson'
                                    : 'Repeat Lesson'}
                            </LoadingButton>
                        )}
                    </Stack>
                )}
            </Stack>

            <RestartLessonDialog
                open={restartDialogOpen}
                loading={isFetchingSelfLaunchedSession}
                onClose={() => setRestartDialogOpen(false)}
                onConfirm={() => {
                    createSelfLaunchSession(false);
                    setRestartDialogOpen(false);
                }}
            />
        </Paper>
    );
};

export default LearningObjectiveItem;
